import { jsPDF } from 'jspdf';
import { renderToStaticMarkup } from 'react-dom/server';
import { createElement } from 'react';
import type { OptimizationResult, SheetLayout } from '@pomoshnik/shared';

import type { CuttingDocument, EdgeAssignment, GrooveAssignment, GrooveType } from '../model/types';
import { computeResultMetrics, DEFAULT_MIN_OFFCUT_MM } from './resultMetrics';
import { buildOffcutList, type OffcutRow } from './offcutList';
import { isCompletePart } from '../model/partsUtils';
import { CutMapCanvas } from '../CutMap';
import { svgToPng, extractSvg } from './svgRaster';

/**
 * Block 14 «Раскрой» — экспорт PDF карты раскроя (PR pdf-export, ADR-0096).
 *
 * Содержимое ЗЕРКАЛИТ печать (`window.print()` + `CutPrintDetails`/`CutPrintMap`):
 * карта КАЖДОГО листа (landscape) + страница метрик «Результат раскроя» +
 * «Список деталей» + «Список остатков». Стек — jsPDF (консистентно с КП
 * `renderQuoteToPdf`; jsPDF уже зависимость v4.2.1). Кириллица — self-hosted
 * PT Sans (без него русские буквы = ▯); механизм загрузки шрифта скопирован
 * вербатим из `renderQuoteToPdf` (module-cache + `__resetFontCacheForTests`).
 *
 * Карта рендерится НАШИМ `CutMapCanvas` (через `renderToStaticMarkup`) → SVG →
 * PNG через canvas (паттерн `svgToPng` из `docs/prototypes/cutting/js/project-io.js`),
 * чтобы геометрия/FLIP-Y/нумерация остатков не разъехались с экраном (A.4 — единый
 * рендер, без дублирования геометрии). Данные метрик/деталей/остатков берутся из
 * тех же источников, что печать: `computeResultMetrics`, `doc.parts`, `buildOffcutList`.
 *
 * Клиентское (P-009): ничего не уходит на сервер.
 */

// ─── Шрифт PT Sans (вербатим из renderQuoteToPdf) ───────────────────────────

const FONT_NAME = 'PTSans';
const FONT_FILES = {
  regular: { url: '/fonts/PTSans-Regular.ttf', vfs: 'PTSans-Regular.ttf' },
  bold: { url: '/fonts/PTSans-Bold.ttf', vfs: 'PTSans-Bold.ttf' },
} as const;

type FontKey = keyof typeof FONT_FILES;

const cachedFontBase64: Record<FontKey, string | null> = { regular: null, bold: null };

async function loadFontBase64(key: FontKey): Promise<string> {
  const cached = cachedFontBase64[key];
  if (cached !== null) return cached;

  const file = FONT_FILES[key];
  const response = await fetch(file.url);
  if (!response.ok) {
    throw new Error(`Не удалось загрузить шрифт (${response.status} ${response.statusText})`);
  }
  const buffer = await response.arrayBuffer();
  if (buffer.byteLength < 1024 || !hasTrueTypeMagic(buffer)) {
    throw new Error(`Шрифт повреждён или не TTF (${buffer.byteLength} bytes)`);
  }
  const base64 = arrayBufferToBase64(buffer);
  cachedFontBase64[key] = base64;
  return base64;
}

function hasTrueTypeMagic(buffer: ArrayBuffer): boolean {
  const head = new Uint8Array(buffer, 0, 4);
  return head[0] === 0x00 && head[1] === 0x01 && head[2] === 0x00 && head[3] === 0x00;
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

/** Reset cache — exposed для unit-test isolation (как renderQuoteToPdf). */
export function __resetFontCacheForTests(): void {
  cachedFontBase64.regular = null;
  cachedFontBase64.bold = null;
}

// ─── Текстовые хелперы (PURE — покрыты golden-тестами) ───────────────────────

/** Сторона кромки → буква подписи (печать рисует мини-SVG; в PDF — текст). */
const EDGE_SIDE_LETTER: Record<keyof EdgeAssignment, string> = {
  top: 'В',
  bottom: 'Н',
  left: 'Л',
  right: 'П',
};

/**
 * Кромки детали → текст. Стороны со значением `!== ''` → буквы (В/Н/Л/П) в
 * порядке `[top, bottom, left, right]`, join `,`; если кромок нет — `'—'`.
 */
export function formatEdges(edges: EdgeAssignment): string {
  const order: (keyof EdgeAssignment)[] = ['top', 'bottom', 'left', 'right'];
  const letters = order.filter((side) => edges[side] !== '').map((side) => EDGE_SIDE_LETTER[side]);
  return letters.length > 0 ? letters.join(',') : '—';
}

const GROOVE_TYPE_LETTER: Record<Exclude<GrooveType, ''>, string> = { v: 'V', u: 'U', l: 'L' };
const GROOVE_SIDE_LETTER: Record<keyof GrooveAssignment, string> = {
  top: 'В',
  right: 'П',
  bottom: 'Н',
  left: 'Л',
};

/**
 * Пазы детали → текст. Для каждой непустой стороны → `${тип}-${сторона}`
 * (например `V-Н`) в порядке `[top, right, bottom, left]`, join `,`; нет — `'—'`.
 */
export function formatGrooves(grooves: GrooveAssignment): string {
  const order: (keyof GrooveAssignment)[] = ['top', 'right', 'bottom', 'left'];
  const parts = order
    .filter((side) => grooves[side] !== '')
    .map(
      (side) =>
        `${GROOVE_TYPE_LETTER[grooves[side] as Exclude<GrooveType, ''>]}-${GROOVE_SIDE_LETTER[side]}`,
    );
  return parts.length > 0 ? parts.join(',') : '—';
}

/** Строка «Список остатков» → ячейки таблицы: № (`ОN`), Размер, Площадь м². */
export function offcutRowCells(row: OffcutRow): string[] {
  return [`О${row.number}`, `${row.length} × ${row.width}`, row.areaM2.toFixed(3)];
}

// ─── Вёрстка (landscape A4) ──────────────────────────────────────────────────

const LAYOUT = {
  pageW: 297,
  pageH: 210,
  marginX: 12,
  marginTop: 15,
  marginBottom: 12,
  rowH: 9,
  rowHeaderH: 10,
  borderLineWidth: 0.3,
  textPaddingX: 2,
  gapAfterTitle: 6,
  gapAfterTable: 8,
} as const;

const FONT_SIZES = {
  pageTitle: 16,
  sectionTitle: 13,
  tableHeader: 10,
  tableData: 10,
} as const;

/** Целевая ширина растеризации SVG-карты (px); высота — по пропорциям листа. */
const SVG_RENDER_W = 1600;

interface RenderCursor {
  y: number;
}

function ensureSpace(pdf: jsPDF, cursor: RenderCursor, needed: number): void {
  if (cursor.y + needed > LAYOUT.pageH - LAYOUT.marginBottom) {
    pdf.addPage();
    cursor.y = LAYOUT.marginTop;
  }
}

function drawPageTitle(pdf: jsPDF, text: string, cursor: RenderCursor): void {
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.pageTitle);
  pdf.setTextColor(0, 0, 0);
  pdf.text(text, LAYOUT.marginX, cursor.y, { baseline: 'top' });
  cursor.y += 8 + LAYOUT.gapAfterTitle;
}

function drawSectionTitle(pdf: jsPDF, text: string, cursor: RenderCursor): void {
  ensureSpace(pdf, cursor, 6 + LAYOUT.gapAfterTitle + LAYOUT.rowH);
  pdf.setFont(FONT_NAME, 'bold');
  pdf.setFontSize(FONT_SIZES.sectionTitle);
  pdf.setTextColor(0, 0, 0);
  pdf.text(text, LAYOUT.marginX, cursor.y, { baseline: 'top' });
  cursor.y += 6 + LAYOUT.gapAfterTitle;
}

/**
 * Bordered-таблица (паттерн `drawBorderedTable` из `renderQuoteToPdf`). Первая
 * колонка — left-aligned, остальные — centered. `headers=null` → без шапки
 * (таблица метрик). Пагинация: шапка повторяется на новой странице.
 */
function drawBorderedTable(
  pdf: jsPDF,
  headers: string[] | null,
  rows: string[][],
  cursor: RenderCursor,
  colWidthsPct: number[],
): void {
  const tableW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const colWidths = colWidthsPct.map((pct) => tableW * pct);

  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(LAYOUT.borderLineWidth);

  const drawHeader = (): void => {
    if (!headers) return;
    pdf.rect(LAYOUT.marginX, cursor.y, tableW, LAYOUT.rowHeaderH);
    let xCursor = LAYOUT.marginX;
    for (let i = 0; i < headers.length - 1; i++) {
      xCursor += colWidths[i] ?? 0;
      pdf.line(xCursor, cursor.y, xCursor, cursor.y + LAYOUT.rowHeaderH);
    }
    pdf.setFont(FONT_NAME, 'bold');
    pdf.setFontSize(FONT_SIZES.tableHeader);
    pdf.setTextColor(0, 0, 0);
    xCursor = LAYOUT.marginX;
    for (let i = 0; i < headers.length; i++) {
      pdf.text(
        headers[i] ?? '',
        xCursor + (colWidths[i] ?? 0) / 2,
        cursor.y + LAYOUT.rowHeaderH / 2,
        {
          align: 'center',
          baseline: 'middle',
        },
      );
      xCursor += colWidths[i] ?? 0;
    }
    cursor.y += LAYOUT.rowHeaderH;
  };

  ensureSpace(pdf, cursor, (headers ? LAYOUT.rowHeaderH : 0) + LAYOUT.rowH);
  drawHeader();

  pdf.setFont(FONT_NAME, 'normal');
  pdf.setFontSize(FONT_SIZES.tableData);
  pdf.setTextColor(0, 0, 0);

  for (const row of rows) {
    if (cursor.y + LAYOUT.rowH > LAYOUT.pageH - LAYOUT.marginBottom) {
      pdf.addPage();
      cursor.y = LAYOUT.marginTop;
      drawHeader();
      pdf.setFont(FONT_NAME, 'normal');
      pdf.setFontSize(FONT_SIZES.tableData);
    }

    pdf.rect(LAYOUT.marginX, cursor.y, tableW, LAYOUT.rowH);
    let xCursor = LAYOUT.marginX;
    for (let i = 0; i < row.length - 1; i++) {
      xCursor += colWidths[i] ?? 0;
      pdf.line(xCursor, cursor.y, xCursor, cursor.y + LAYOUT.rowH);
    }

    xCursor = LAYOUT.marginX;
    for (let i = 0; i < row.length; i++) {
      const cellValue = row[i] || '—';
      const colW = colWidths[i] ?? 0;
      if (i === 0) {
        pdf.text(cellValue, xCursor + LAYOUT.textPaddingX, cursor.y + LAYOUT.rowH / 2, {
          baseline: 'middle',
        });
      } else {
        pdf.text(cellValue, xCursor + colW / 2, cursor.y + LAYOUT.rowH / 2, {
          align: 'center',
          baseline: 'middle',
        });
      }
      xCursor += colW;
    }
    cursor.y += LAYOUT.rowH;
  }

  cursor.y += LAYOUT.gapAfterTable;
}

/**
 * Печатная цветовая схема карты, вшитая ПРЯМО в SVG. При растеризации в PNG
 * (`svgToPng`) внешний CSS (`cutting-theme.css`) НЕ применяется → элементы без
 * inline-заливки (`.free-rect` и его текст) получили бы дефолтную ЧЁРНУЮ заливку,
 * а CSS-рамка `.sc-svg-canvas` потерялась бы. Поэтому задаём заливки/обводки/цвет
 * текста через `<style>` внутри самого SVG (PDF-only; экранную карту/`window.print`
 * не трогаем): детали — белые с чёрной рамкой, остатки — серые `#e0e0e0` с чёрной
 * рамкой, весь текст — чёрный.
 */
const PRINT_STYLE =
  '<style>' +
  '.part-group rect{fill:#ffffff !important;stroke:#000000 !important;stroke-width:3 !important;}' +
  '.free-rect{fill:#e0e0e0 !important;stroke:#000000 !important;stroke-width:3 !important;stroke-dasharray:none !important;}' +
  'text{fill:#000000 !important;}' +
  '</style>';

/** Вставить `PRINT_STYLE` сразу после открывающего тега `<svg …>`. */
export function injectPrintStyles(svg: string): string {
  return svg.replace('>', '>' + PRINT_STYLE);
}

/** Карта одного листа: SVG нашим рендером → PNG → вписать в страницу. */
async function drawSheetMap(
  pdf: jsPDF,
  doc: CuttingDocument,
  sheet: SheetLayout,
  threshold: number,
  idx: number,
  total: number,
  titleSuffix: string,
  offcutStart: number,
  cursor: RenderCursor,
  showEdges: boolean,
  showGrooves: boolean,
): Promise<void> {
  const svgMarkup = renderToStaticMarkup(
    createElement(CutMapCanvas, {
      sheet,
      parts: doc.parts,
      threshold,
      idx,
      total,
      titleSuffix,
      offcutStart,
      enablePanZoom: false,
      showEdges,
      showGrooves,
    }),
  );
  const renderH = Math.max(1, Math.round((SVG_RENDER_W * sheet.sheetWidth) / sheet.sheetLength));
  const svg = injectPrintStyles(
    extractSvg(svgMarkup).replace(/^<svg /, `<svg width="${SVG_RENDER_W}" height="${renderH}" `),
  );
  const png = await svgToPng(svg, SVG_RENDER_W, renderH);

  const availW = LAYOUT.pageW - 2 * LAYOUT.marginX;
  const availH = LAYOUT.pageH - LAYOUT.marginBottom - cursor.y;
  const aspect = sheet.sheetLength / sheet.sheetWidth; // ширина:высота листа
  let w = availW;
  let h = w / aspect;
  if (h > availH) {
    h = availH;
    w = h * aspect;
  }
  const x = LAYOUT.marginX + (availW - w) / 2;
  pdf.addImage(png, 'PNG', x, cursor.y, w, h);
  // Видимая рамка листа материала (на растеризованном PNG CSS-граница не видна).
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.4);
  pdf.rect(x, cursor.y, w, h);
  cursor.y += h;
}

/**
 * Render карты раскроя → PDF Blob. Содержимое зеркалит печать: карта каждого
 * листа (по странице) + метрики + «Список деталей» + «Список остатков».
 */
export async function renderCuttingPdf(
  doc: CuttingDocument,
  result: OptimizationResult,
  opts?: { showEdges?: boolean; showGrooves?: boolean },
): Promise<Blob> {
  // Тогглы карты (Кромки/Пазы): default true (обратная совместимость с buildPdf
  // по умолчанию). ShareFormatDialog прокидывает экранный выбор из стора.
  const showEdges = opts?.showEdges ?? true;
  const showGrooves = opts?.showGrooves ?? true;
  const threshold = doc.settings.minOffcutSize ?? DEFAULT_MIN_OFFCUT_MM;
  const { startBySheet, rows: offcutRows } = buildOffcutList(result, threshold);
  const metrics = computeResultMetrics(result, doc.parts, doc.settings.minOffcutSize);
  const total = result.sheets.length;

  const [regularBase64, boldBase64] = await Promise.all([
    loadFontBase64('regular'),
    loadFontBase64('bold'),
  ]);

  // Карта раскроя широкая → landscape (как печать карты, page 1 @media print).
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
  pdf.addFileToVFS(FONT_FILES.regular.vfs, regularBase64);
  pdf.addFont(FONT_FILES.regular.vfs, FONT_NAME, 'normal');
  pdf.addFileToVFS(FONT_FILES.bold.vfs, boldBase64);
  pdf.addFont(FONT_FILES.bold.vfs, FONT_NAME, 'bold');
  pdf.setFont(FONT_NAME, 'normal');
  pdf.setTextColor(0, 0, 0);

  // A. Карта КАЖДОГО листа — отдельная страница (первая уже есть).
  for (let i = 0; i < total; i++) {
    const sheet = result.sheets[i];
    if (!sheet) continue;
    if (i > 0) pdf.addPage();
    const cursor: RenderCursor = { y: LAYOUT.marginTop };

    const sheetDims = `${Math.round(sheet.sheetLength)} × ${Math.round(sheet.sheetWidth)}`;
    const titleSuffix = sheet.isOffcut ? ` · Остаток ${sheetDims}` : ` — ${sheetDims}`;
    drawPageTitle(pdf, `Карта раскроя — лист ${i + 1} из ${total}${titleSuffix}`, cursor);

    await drawSheetMap(
      pdf,
      doc,
      sheet,
      threshold,
      i,
      total,
      titleSuffix,
      startBySheet[i] ?? 0,
      cursor,
      showEdges,
      showGrooves,
    );
  }

  // B. Страница «Результат раскроя» — метрики (зеркалит видимые метрики экрана).
  pdf.addPage();
  const cursor: RenderCursor = { y: LAYOUT.marginTop };
  drawPageTitle(pdf, 'Результат раскроя', cursor);
  drawBorderedTable(
    pdf,
    null,
    [
      ['Проект', doc.meta.name || '—'],
      ['Материал', doc.meta.materialText || '—'],
      ['Толщина', doc.meta.materialThickness != null ? `${doc.meta.materialThickness} мм` : '—'],
      ['Количество листов', String(metrics.sheetCount)],
      ['Длина кромки 1', `${metrics.edge1Length.toFixed(1)} м`],
      ['Длина кромки 2', `${metrics.edge2Length.toFixed(1)} м`],
      ['Использовано остатков', String(metrics.usedOffcutCount)],
      ['КИМ', `${metrics.kimPercent.toFixed(1)} %`],
    ],
    cursor,
    [0.4, 0.6],
  );

  // C. «Список деталей» — текстовая таблица (печать рисует мини-SVG; в PDF — текст).
  const parts = doc.parts.filter(isCompletePart);
  if (parts.length > 0) {
    drawSectionTitle(pdf, 'Список деталей', cursor);
    drawBorderedTable(
      pdf,
      ['№', 'Наименование', 'Длина', 'Ширина', 'Кол-во', 'Кромки', 'Пазы', 'Обработка'],
      parts.map((p, i) => [
        String(i + 1),
        p.name || '—',
        String(p.length),
        String(p.width),
        String(p.qty),
        formatEdges(p.edges),
        formatGrooves(p.grooves),
        p.processing || '—',
      ]),
      cursor,
      [0.05, 0.25, 0.1, 0.1, 0.08, 0.16, 0.16, 0.1],
    );
  }

  // D. «Список остатков» — пустой список → секцию НЕ рисуем.
  if (offcutRows.length > 0) {
    drawSectionTitle(pdf, 'Список остатков', cursor);
    drawBorderedTable(
      pdf,
      ['№', 'Размер', 'Площадь, м²'],
      offcutRows.map(offcutRowCells),
      cursor,
      [0.2, 0.5, 0.3],
    );
  }

  return pdf.output('blob');
}
